<template>
    <section id="process" class="process-section">
        <div class="container process-layout">
            <div class="process-introduction">
                <div class="section-identifier">
                    {{ t.process.section }}
                </div>

                <p class="eyebrow">
                    <span></span>
                    {{ t.process.eyebrow }}
                </p>

                <h2>
                    {{ t.process.titleFirst }}
                    <em>{{ t.process.titleSecond }}</em>
                </h2>
            </div>

            <ol class="process-list">
                <li v-for="(step, index) in t.process.steps"
                    :key="step.title">
                    <span class="step-number">
                        {{ String(index + 1).padStart(2, '0') }}
                    </span>

                    <div class="step-information">
                        <h3>{{ step.title }}</h3>
                        <p>{{ step.description }}</p>
                    </div>

                    <div class="step-icon" aria-hidden="true">
                        <svg v-if="index === 0"
                             viewBox="0 0 24 24">
                            <path d="M4 5h16v11H8l-4 3V5Z" />
                            <path d="M8 9h8M8 12h5" />
                        </svg>

                        <svg v-else-if="index === 1"
                             viewBox="0 0 24 24">
                            <circle cx="11" cy="11" r="7" />
                            <path d="m16 16 5 5M8 11h6M11 8v6" />
                        </svg>

                        <svg v-else-if="index === 2"
                             viewBox="0 0 24 24">
                            <path d="m14 6 4-3 3 3-3 4" />
                            <path d="M16 8 8 16M5 14l5 5-7 2 2-7Z" />
                        </svg>

                        <svg v-else
                             viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9" />
                            <path d="m8 12 2.5 2.5L16 9" />
                        </svg>
                    </div>
                </li>
            </ol>
        </div>
    </section>
</template>

<script>
export default {
  name: 'ProcessSection',

  props: {
    t: {
      type: Object,
      required: true
    }
  }
}
</script>

<style scoped>
    .process-section {
        padding-block: var(--section-padding);
        background-color: var(--color-concrete);
    }

    .process-layout {
        display: grid;
        grid-template-columns: 43% 1fr;
        gap: 8%;
    }

    .process-introduction {
        position: sticky;
        top: calc(var(--navbar-height) + 45px);
        align-self: start;
    }

    .section-identifier {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 62px;
        color: #77736c;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.2em;
        text-transform: uppercase;
    }

        .section-identifier > span {
            display: grid;
            width: 33px;
            height: 33px;
            place-items: center;
            color: var(--color-orange-dark);
            border: 1px solid var(--color-border);
            font-size: 9px;
        }

    .eyebrow {
        display: flex;
        align-items: center;
        gap: 13px;
        margin-bottom: 25px;
        color: var(--color-orange-dark);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.21em;
        text-transform: uppercase;
    }

        .eyebrow span {
            width: 32px;
            height: 2px;
            background-color: currentColor;
        }

    h2 {
        max-width: 680px;
        margin: 0;
        color: var(--color-charcoal);
        font-family: var(--font-heading);
        font-size: clamp(48px, 4.8vw, 74px);
        font-weight: 400;
        letter-spacing: -0.045em;
        line-height: 0.98;
    }

        h2 em {
            display: block;
            color: var(--color-orange);
            font-weight: 400;
        }

    .process-list {
        list-style: none;
        border-top: 1px solid var(--color-border);
    }

        .process-list li {
            display: grid;
            min-height: 170px;
            align-items: center;
            grid-template-columns: 70px 1fr 55px;
            gap: 25px;
            border-bottom: 1px solid var(--color-border);
            transition: background-color 0.25s ease, padding 0.25s ease;
        }

            .process-list li:hover {
                padding-inline: 20px;
                background-color: #e9e4da;
            }

    .step-number {
        align-self: start;
        padding-top: 48px;
        color: var(--color-orange-dark);
        font-size: 10px;
        font-weight: 800;
    }

    .step-information {
        padding-block: 35px;
    }

        .step-information h3 {
            margin: 0 0 10px;
            color: var(--color-charcoal);
            font-family: var(--font-heading);
            font-size: 28px;
            font-weight: 400;
            line-height: 1.15;
        }

        .step-information p {
            max-width: 480px;
            margin: 0;
            color: var(--color-muted);
            font-size: 14px;
            line-height: 1.7;
        }

    .step-icon {
        display: grid;
        width: 48px;
        height: 48px;
        place-items: center;
        color: var(--color-orange-dark);
        border: 1px solid rgba(189, 82, 25, 0.35);
    }

        .step-icon svg {
            width: 23px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.5;
        }

    @media (max-width: 900px) {
        .process-layout {
            grid-template-columns: 1fr;
            gap: 60px;
        }

        .process-introduction {
            position: static;
        }

        .section-identifier {
            margin-bottom: 40px;
        }
    }

    @media (max-width: 560px) {
        .process-layout {
            gap: 45px;
        }

        .process-list li {
            min-height: 150px;
            grid-template-columns: 45px 1fr;
            gap: 12px;
        }

            .process-list li:hover {
                padding-inline: 10px;
            }

        .step-number {
            padding-top: 38px;
        }

        .step-information {
            padding-block: 28px;
        }

        .step-icon {
            display: none;
        }

        .step-information h3 {
            font-size: 25px;
        }
    }
</style>